import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { getTextField } from '@lender/utils';
import { Button } from '@mui/material';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { object } from 'yup';

import useForm from 'hooks/useForm';
import { getDefaultErrorMessage } from 'utils/axios';
import { logger } from 'utils/logger';

import { getDocumentExtractionRequestAction, rejectDocumentById } from 'store/documents/actions';
import { closeDialogAction, showErrorMessageAction, showMessageAction } from 'store/global/actions';

import { DocumentTestIds } from '../../../document.testIds';
import { getIsErrorStatusForbiddenOrNotFound } from '../EditExtractionFields.utils';

const schema = object().shape({
  rejectReason: getTextField(),
});

const RejectDocumentForm = ({
  isLoading,
  setIsLoading,
  formRef = null,
  documentId = '',
  handleReject,
}) => {
  const dispatch = useDispatch();

  const { form, isFormChanged, validationErrors, setSchema, handleChange } = useForm({
    rejectReason: '',
  });
  const isSubmitDisabled = !isFormChanged || !!validationErrors.rejectReason || isLoading;

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  const getAssignedDocumentWithReset = useCallback(async () => {
    handleReject();

    await dispatch(getDocumentExtractionRequestAction());

    dispatch(closeDialogAction());
  }, [dispatch, handleReject]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();
      const rejectPayload = {
        documentId,
        reason: form.rejectReason,
        silent: true,
      };

      setIsLoading(true);

      dispatch(rejectDocumentById(rejectPayload))
        .then(() =>
          dispatch(showMessageAction({ message: 'Successfully rejected', variant: 'success' })),
        )
        .catch(err => {
          dispatch(showErrorMessageAction({ message: getDefaultErrorMessage(err), err }));
          logger.error(err);
        })
        .then(() => getAssignedDocumentWithReset())
        .catch(async err => {
          logger.error(err);

          if (getIsErrorStatusForbiddenOrNotFound(err)) {
            await getAssignedDocumentWithReset();
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [documentId, form.rejectReason, setIsLoading, dispatch, getAssignedDocumentWithReset],
  );

  return (
    <form
      noValidate
      className="flex flex-col justify-center w-400 p-24"
      data-ischanged={isFormChanged}
      data-testid={DocumentTestIds.REJECT_DOCUMENT_FORM}
      ref={formRef}
      onSubmit={onSubmit}
    >
      <TextFieldCDS
        autoFocus
        fullWidth
        className="mb-24"
        data-testid={DocumentTestIds.REJECT_REASON_INPUT}
        disabled={isLoading}
        error={!!validationErrors.rejectReason}
        helperText={validationErrors.rejectReason}
        id="rejectReason"
        label="Reject Reason"
        maxLength={150}
        name="rejectReason"
        value={form.rejectReason}
        variant="outlined"
        onChange={handleChange}
      />
      <Button
        className="self-end"
        data-testid={DocumentTestIds.REJECT_DOCUMENT_SUBMIT_BUTTON}
        disabled={isSubmitDisabled}
        type="submit"
        variant="contained"
      >
        Add
      </Button>
    </form>
  );
};

RejectDocumentForm.propTypes = {
  handleReject: PropTypes.func.isRequired,
  formRef: PropTypes.shape({ current: PropTypes.object }),
  documentId: PropTypes.string,
  ...WithLoadingPropTypes,
};

export default withLoading(RejectDocumentForm);
